type Validatable = { value: unknown };
type UndefinedOrString = string | undefined;

const INVALID_VALUE_ERROR = "Invalid value";

function validateEmptyString(field: Validatable): UndefinedOrString {
  if (typeof field.value === "string" && field.value.trim() === "") {
    return "This field is required";
  }
  return undefined;
}

function validateValidDate(field: Validatable): UndefinedOrString {
  if (field.value instanceof Date && isNaN(field.value.getTime())) {
    return "Invalid date";
  }
  return undefined;
}

function validatePositiveNumber(field: Validatable): UndefinedOrString {
  if (typeof field.value !== "string") {
    return INVALID_VALUE_ERROR;
  }
  if (Number(field.value) < 0) {
    return "Must be a positive number";
  }
  return undefined;
}

function validateGreaterThanZero(field: Validatable): UndefinedOrString {
  if (typeof field.value !== "string") {
    return INVALID_VALUE_ERROR;
  }
  if (Number(field.value) <= 0) {
    return "Must be greater than zero";
  }
  return undefined;
}

function validateZipCode(field: Validatable): UndefinedOrString {
  if (typeof field.value !== "string") {
    return INVALID_VALUE_ERROR;
  }
  if (!/^\d{5}(-\d{4})?$/.test(field.value)) {
    return "Must be a valid zip code (5 digits or ZIP+4 format XXXXX-XXXX)";
  }
  return undefined;
}

function validateBetweenNumbers(
  min: number,
  max: number,
): (field: Validatable) => UndefinedOrString {
  return (field: Validatable): string | undefined => {
    if (
      field.value === "" ||
      field.value === null ||
      field.value === undefined
    ) {
      return undefined;
    }

    const typeOfValue = typeof field.value;
    const value =
      typeOfValue === "string" || typeOfValue === "number"
        ? Number(field.value)
        : NaN;

    if (Number.isNaN(value)) {
      return INVALID_VALUE_ERROR;
    }

    if (value < min || value > max) {
      return `Must be between ${min} and ${max}`;
    }

    return undefined;
  };
}

function validateEmail(field: Validatable): UndefinedOrString {
  if (field.value === "" || field.value === null || field.value === undefined) {
    return undefined;
  }
  if (typeof field.value !== "string") {
    return INVALID_VALUE_ERROR;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
    return "Must be a valid email address";
  }
  return undefined;
}

export {
  validateEmptyString,
  validateValidDate,
  validatePositiveNumber,
  validateBetweenNumbers,
  validateGreaterThanZero,
  validateEmail,
  validateZipCode,
};
